import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandName } from "@/components/ui/BrandName";
import { AppFooter } from "@/components/ui/AppFooter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import type { AppLanguage } from "@/i18n/config";
import { translateSystemEnumLabel } from "@/i18n/contentSemantics";
import { hasPermission, type AppPermission } from "@/lib/permissions";
import type { AppUser } from "@/types/domain";
import {
  Activity,
  BarChart3,
  BookUser,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileUser,
  FileText,
  FileUp,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Settings,
  Shield,
  ShieldPlus,
  Target,
  UserCog,
  UserPlus,
  Users,
  WalletCards,
  X,
  type LucideIcon,
} from "lucide-react";

interface NavigationItem {
  path: string;
  label: string;
  icon: LucideIcon;
  permission?: AppPermission;
  permissions?: AppPermission[];
  adminOnly?: boolean;
}

interface NavigationSection {
  id: string;
  label: string;
  items: NavigationItem[];
  includesFinancialMenu?: boolean;
}

const financialNavItem: NavigationItem = {
  path: "/financeiro",
  label: "Financeiro",
  icon: DollarSign,
  permission: "finance.read",
};

const financialSubNavItems: NavigationItem[] = [
  { path: "/financeiro/dre-gerencial", label: "DRE Gerencial", icon: FileText, permission: "managerial_income_statement.read" },
  { path: "/financeiro/despesas", label: "Despesas", icon: WalletCards, permission: "expenses.read" },
  { path: "/financeiro/repasses", label: "Repasses profissionais", icon: CreditCard, permission: "payouts.read" },
];

const navigationSections: NavigationSection[] = [
  {
    id: "inicio",
    label: "Início",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard.read" },
      { path: "/dashboard/visao-gerencial", label: "Visão gerencial", icon: BarChart3, permission: "management_overview.read" },
    ],
  },
  {
    id: "rotina-clinica",
    label: "Rotina clínica",
    items: [
      { path: "/agenda", label: "Agenda", icon: Calendar, permission: "appointments.read" },
      { path: "/atendimento", label: "Atendimento", icon: Activity, permission: "attendance.read" },
      { path: "/pacientes", label: "Pacientes", icon: FileUser, permission: "patients.read" },
      { path: "/ficha-avaliacao", label: "Prontuário", icon: ClipboardList, permissions: ["patients.read", "medical_records.read"] },
      { path: "/atendimento/producao", label: "Produção clínica", icon: ClipboardCheck, permission: "clinical_production.read" },
    ],
  },
  {
    id: "comercial",
    label: "Comercial",
    items: [
      { path: "/leads", label: "Leads", icon: UserPlus, permission: "leads.read" },
      { path: "/marketing", label: "Marketing", icon: Megaphone, permission: "marketing.read" },
      { path: "/metas", label: "Metas", icon: Target, permission: "goals.read" },
    ],
  },
  {
    id: "gestao",
    label: "Gestão",
    includesFinancialMenu: true,
    items: [
      { path: "/profissionais", label: "Profissionais", icon: Users, permission: "professionals.read" },
    ],
  },
  {
    id: "administracao",
    label: "Administração",
    items: [
      { path: "/catalogo", label: "Catálogo", icon: ClipboardList, adminOnly: true },
      { path: "/usuarios", label: "Usuários", icon: BookUser, permission: "users.read", adminOnly: true },
      { path: "/configuracoes", label: "Configurações", icon: Settings, permission: "appointment_statuses.manage", adminOnly: true },
    ],
  },
];

const superAdminNavigationSections: NavigationSection[] = [
  {
    id: "administracao",
    label: "Administração",
    items: [
      { path: "/super-admin/clinicas", label: "Clínicas", icon: Shield },
      { path: "/super-admin/importa-zen-fisio", label: "Importa Zen Fisio", icon: FileUp },
      { path: "/super-admin/super-admins", label: "Super Admins", icon: ShieldPlus },
      { path: "/super-admin/auditoria", label: "Auditoria", icon: UserCog },
    ],
  },
];

const NAVIGATION_LABELS = {
  "Início": "Início",
  "Rotina clínica": "Rotina clínica",
  Comercial: "Comercial",
  Gestão: "Gestão",
  Administração: "Administração",
  Dashboard: "Dashboard",
  "Visão gerencial": "Visão gerencial",
  Agenda: "Agenda",
  Atendimento: "Atendimento",
  Pacientes: "Pacientes",
  Prontuário: "Prontuário",
  "Produção clínica": "Produção clínica",
  Leads: "Leads",
  Marketing: "Marketing",
  Metas: "Metas",
  Profissionais: "Profissionais",
  Catálogo: "Catálogo",
  Usuários: "Usuários",
  Configurações: "Configurações",
  Financeiro: "Financeiro",
  "DRE Gerencial": "DRE Gerencial",
  Despesas: "Despesas",
  "Repasses profissionais": "Repasses profissionais",
  Clínicas: "Clínicas",
  "Importa Zen Fisio": "Importa Zen Fisio",
  "Super Admins": "Super Admins",
  Auditoria: "Auditoria",
  "Visão geral": "Visão geral",
} as const;

export function navigationLabel(value: string, language: AppLanguage) {
  return translateSystemEnumLabel(value, NAVIGATION_LABELS, language);
}

const userMenuContentClass =
  "w-72 rounded-2xl border-[#33433d] bg-[#111917] p-2 text-[#f3f0e8] shadow-2xl shadow-black/35";

const userMenuSeparatorClass = "my-2 bg-[#33433d]";

const userMenuControlClass =
  "[&_button]:text-[#f3f0e8] [&_button]:hover:bg-[#20312b] [&_button]:focus:bg-[#20312b] [&_svg]:text-[#a5b8ad]";

function getUserInitial(name?: string | null): string {
  return name?.charAt(0).toUpperCase() ?? "?";
}

function isSuperAdminRole(role?: string | null): boolean {
  return role?.toUpperCase() === "SUPER_ADMIN";
}

function canViewNavigationItem(user: AppUser, item: NavigationItem): boolean {
  if (item.adminOnly) {
    return user.role?.toUpperCase() === "ADMIN";
  }

  if (item.permissions) {
    return item.permissions.every((permission) => hasPermission(user, permission));
  }

  return !item.permission || hasPermission(user, item.permission);
}

function UserMenuOption({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2 px-2 py-2">
      <div className="space-y-0.5">
        <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#a5b8ad]">{label}</p>
        <p className="text-xs leading-snug text-[#d5ddd6]">{description}</p>
      </div>
      {children}
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [financialMenuOpen, setFinancialMenuOpen] = useState(false);
  const { user, logout, clinics, selectedClinic, selectClinic } = useAuth();
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isFinancialSectionActive = location.pathname === financialNavItem.path || location.pathname.startsWith("/financeiro/");

  useEffect(() => {
    setFinancialMenuOpen(isFinancialSectionActive);
  }, [isFinancialSectionActive]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleClinicChange = (clinicId: string) => {
    const clinic = clinics.find((item) => item.id === clinicId) ?? null;
    if (clinic) {
      selectClinic(clinic);
    }
  };

  const authenticatedUser = user as AppUser | null;

  if (!authenticatedUser) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = isSuperAdminRole(authenticatedUser.role);
  const isAdmin = authenticatedUser.role?.toUpperCase() === "ADMIN";
  const visibleFinancialSubNavItems = isSuperAdmin
    ? []
    : isAdmin
      ? financialSubNavItems
      : financialSubNavItems.filter((item) => canViewNavigationItem(authenticatedUser, item));
  const canViewFinancialPage = isSuperAdmin || isAdmin || canViewNavigationItem(authenticatedUser, financialNavItem);
  const hasFinancialNavigation = canViewFinancialPage || visibleFinancialSubNavItems.length > 0;
  const visibleFinancialNavItems = canViewFinancialPage
    ? [financialNavItem, ...visibleFinancialSubNavItems]
    : visibleFinancialSubNavItems;
  const visibleNavigationSections = (isSuperAdmin ? superAdminNavigationSections : navigationSections)
    .map((section) => ({
      ...section,
      items: isSuperAdmin || isAdmin
        ? section.items
        : section.items.filter((item) => canViewNavigationItem(authenticatedUser, item)),
    }))
    .filter((section) => section.items.length > 0 || (section.includesFinancialMenu && hasFinancialNavigation));

  return (
    <div className="min-h-screen bg-clinic-bg" data-testid="app-layout">
      <header className="fixed left-0 right-0 top-0 z-50 flex h-16 items-center justify-between border-b border-clinic-border bg-clinic-surface px-4 lg:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="rounded-xl p-2 hover:bg-clinic-surface-hover"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} className="text-clinic-text" />
        </button>
        <BrandName as="h1" className="text-xxl text-clinic-primary">
          Fluxus
        </BrandName>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex h-10 w-10 items-center justify-center rounded-full bg-clinic-primary font-medium text-white">
              {getUserInitial(authenticatedUser.name)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={userMenuContentClass}>
            <div className="px-3 py-2">
              <p className="notranslate font-medium text-[#f3f0e8]" data-no-translate="true" translate="no">{authenticatedUser.name}</p>
              <p className="notranslate text-sm text-[#a5b8ad]" data-no-translate="true" translate="no">{authenticatedUser.email}</p>
            </div>
            <DropdownMenuSeparator className={userMenuSeparatorClass} />
            <UserMenuOption label={t("Aparência")} description={t("Alterne entre tema claro e escuro.")}>
              <ThemeToggle variant="menu" className="text-[#f3f0e8] hover:bg-[#20312b] hover:text-white" />
            </UserMenuOption>
            <UserMenuOption label={t("Idioma da interface")} description={t("Escolha o idioma usado nos menus e páginas.")}>
              <LanguageSwitcher variant="menu" className={`px-0 py-0 ${userMenuControlClass}`} />
            </UserMenuOption>
            <UserMenuOption label={t("Moeda do sistema")} description={t("Moeda usada nos valores financeiros.")}>
              <CurrencySwitcher variant="menu" className={`px-0 py-0 ${userMenuControlClass}`} />
            </UserMenuOption>
            <DropdownMenuSeparator className={userMenuSeparatorClass} />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer items-start gap-3 text-[#ff9b8d] focus:bg-[#3a211f] focus:text-[#ffb3a8]">
              <LogOut size={16} className="mt-0.5 shrink-0" />
              <span className="flex flex-col">
                <span className="font-medium">{t("Sair")}</span>
                <span className="text-xs text-[#ffb3a8]/80">{t("Termine a sessão neste dispositivo.")}</span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {sidebarOpen ? (
        <div
          className="fixed inset-0 z-50 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`
          fixed left-0 top-0 z-50 h-full w-72 border-r border-clinic-border bg-clinic-surface
          transform transition-transform duration-300 ease-in-out lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b border-clinic-border px-6">
            <div>
              <BrandName as="h1" className="text-4xl text-clinic-primary">
                Fluxus
              </BrandName>
              <p className="text-xs uppercase tracking-[0.22em] text-clinic-text-muted">
                {isSuperAdmin ? t("Control Tower") : t("Workspace")}
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-xl p-2 hover:bg-clinic-surface-hover lg:hidden"
            >
              <X size={20} className="text-clinic-text" />
            </button>
          </div>

          {!isSuperAdmin && clinics.length > 0 ? (
            <div className="border-b border-clinic-border p-4">
              <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-clinic-text-muted">
                {t("Clínica")}
              </label>
              <Select value={selectedClinic?.id} onValueChange={handleClinicChange}>
                <SelectTrigger className="h-11 w-full rounded-xl" data-testid="clinic-selector">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-clinic-primary" />
                    <SelectValue placeholder={t("Selecione a clínica")} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <nav className="flex-1 space-y-5 overflow-y-auto p-4" aria-label={t("Navegação principal")}>
            {visibleNavigationSections.map((section) => (
              <section key={section.id} aria-labelledby={`nav-section-${section.id}`} className="space-y-1">
                <h2
                  id={`nav-section-${section.id}`}
                  className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-clinic-text-muted"
                >
                  {navigationLabel(section.label, language)}
                </h2>

                {section.includesFinancialMenu && hasFinancialNavigation ? (
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => setFinancialMenuOpen((isOpen) => !isOpen)}
                      aria-label={t("Alternar menu Financeiro")}
                      aria-expanded={financialMenuOpen}
                      data-testid="toggle-financeiro"
                      className={`
                        flex w-full items-center gap-3 rounded-xl px-4 py-3 font-medium transition-all
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinic-primary focus-visible:ring-offset-2
                        ${
                          isFinancialSectionActive
                            ? "bg-clinic-primary text-white"
                            : "text-clinic-text-secondary hover:bg-clinic-surface-hover hover:text-clinic-text"
                        }
                      `}
                    >
                      <DollarSign size={20} />
                      <span className="flex-1 text-left">{navigationLabel(financialNavItem.label, language)}</span>
                      {financialMenuOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>

                    {financialMenuOpen ? (
                      <div className="ml-4 space-y-1 border-l border-clinic-border pl-2">
                        {visibleFinancialNavItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;

                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${item.path.replace("/", "")}`}
                              className={`
                                flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinic-primary focus-visible:ring-offset-2
                                ${
                                  isActive
                                    ? "bg-clinic-primary text-white"
                                    : "text-clinic-text-secondary hover:bg-clinic-surface-hover hover:text-clinic-text"
                                }
                              `}
                            >
                              <Icon size={18} />
                              {navigationLabel(item.path === financialNavItem.path ? "Visão geral" : item.label, language)}
                            </Link>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.path ||
                    (location.pathname.startsWith(`${item.path}/`) && item.path !== "/dashboard" && item.path !== "/atendimento");

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                      className={`
                        flex items-center gap-3 rounded-xl px-4 py-3 font-medium transition-all
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinic-primary focus-visible:ring-offset-2
                        ${
                          isActive
                            ? "bg-clinic-primary text-white"
                            : "text-clinic-text-secondary hover:bg-clinic-surface-hover hover:text-clinic-text"
                        }
                      `}
                    >
                      <Icon size={20} />
                      {navigationLabel(item.label, language)}
                    </Link>
                  );
                })}
              </section>
            ))}
          </nav>

          <div className="border-t border-clinic-border p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-all hover:bg-clinic-surface-hover"
                  data-testid="user-menu-btn"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-clinic-primary font-medium text-white">
                    {getUserInitial(authenticatedUser.name)}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-clinic-text">{authenticatedUser.name}</p>
                    <p className="text-xs capitalize text-clinic-text-muted">
                      {authenticatedUser.role}
                    </p>
                  </div>
                  <ChevronDown size={16} className="text-clinic-text-muted" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={userMenuContentClass}>
                <div className="border-b border-[#33433d] px-3 py-2">
                  <p className="notranslate font-medium text-[#f3f0e8]" data-no-translate="true" translate="no">{authenticatedUser.name}</p>
                  <p className="notranslate text-sm text-[#a5b8ad]" data-no-translate="true" translate="no">{authenticatedUser.email}</p>
                </div>
                <UserMenuOption label={t("Aparência")} description={t("Alterne entre tema claro e escuro.")}>
                  <ThemeToggle variant="menu" className="text-[#f3f0e8] hover:bg-[#20312b] hover:text-white" />
                </UserMenuOption>
                <UserMenuOption label={t("Idioma da interface")} description={t("Escolha o idioma usado nos menus e páginas.")}>
                  <LanguageSwitcher variant="menu" className={`px-0 py-0 ${userMenuControlClass}`} />
                </UserMenuOption>
                <UserMenuOption label={t("Moeda do sistema")} description={t("Moeda usada nos valores financeiros.")}>
                  <CurrencySwitcher variant="menu" className={`px-0 py-0 ${userMenuControlClass}`} />
                </UserMenuOption>
                <DropdownMenuSeparator className={userMenuSeparatorClass} />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer items-start gap-3 text-[#ff9b8d] focus:bg-[#3a211f] focus:text-[#ffb3a8]"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} className="mt-0.5 shrink-0" />
                  <span className="flex flex-col">
                    <span className="font-medium">{t("Sair")}</span>
                    <span className="text-xs text-[#ffb3a8]/80">{t("Termine a sessão neste dispositivo.")}</span>
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      <main className="min-h-screen pt-16 lg:pl-72 lg:pt-0">
        <div className="flex min-h-[calc(100vh-4rem)] flex-col p-4 lg:min-h-screen lg:p-8">
          <div className="flex-1">{children}</div>
          <AppFooter variant="compact" className="mt-6" />
        </div>
      </main>
    </div>
  );
}
